import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FaMap } from 'react-icons/fa';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import { useProject } from '@contexts/Project.context';
import { useToast } from '@contexts/Toast.context';
import { getLayerByCustomId } from '@utils/map/helpers';
import { getMaptilerKey } from '@utils/api';
import {
	BASE_MAP_LAYER_ID,
	getBaseMaps,
	FALLBACK_MAP,
	PREFERRED_MAP,
	Z_INDEX,
	ICON_SIZE,
} from './constants';
import {
	SelectorContainer,
	SelectorButton,
	SelectorPanel,
	PanelHeader,
	PanelTitle,
	IndicatorText,
	OptionsList,
	OptionButton,
	OptionIcon,
} from './StyledBaseMapComponents';

/**
 * BaseMapSelector - Base map picker for the map controls rail.
 *
 * Renders a button plus its panel; placement is handled by MapControlsCluster.
 */
const BaseMapSelector = () => {
	const { mapObject } = useProject();
	const { addToast } = useToast();
	const { project_uuid } = useParams();
	const [isOpen, setIsOpen] = useState(false);
	const [selectedMap, setSelectedMap] = useState(FALLBACK_MAP);
	const panelRef = useRef(null);
	const baseMapLayerRef = useRef(null);
	const hasUserSelected = useRef(false);

	const [tileLoadFailed, setTileLoadFailed] = useState(false);

	const { data: maptilerKey, isError: maptilerKeyError } = useQuery({
		queryKey: ['maptiler-key', project_uuid],
		queryFn: () => getMaptilerKey(project_uuid),
		enabled: !!project_uuid,
		staleTime: Infinity,
		retry: 1,
	});

	// Notify user if the key fetch itself failed
	useEffect(() => {
		if (maptilerKeyError) {
			addToast({
				id: 'maptiler-key-error',
				title: 'MapTiler maps are unavailable',
				message:
					'Could not retrieve the map key. Satellite, Terrain, and Streets maps are disabled.',
				bg: 'warning',
				delay: 5000,
			});
		}
	}, [maptilerKeyError]);

	const maptilerAvailable = !!maptilerKey && !tileLoadFailed;
	const baseMaps = useMemo(() => getBaseMaps(maptilerKey), [maptilerKey]);

	// Switch to preferred map (satellite) once the key is available
	useEffect(() => {
		if (maptilerAvailable && !hasUserSelected.current) setSelectedMap(PREFERRED_MAP);
	}, [maptilerAvailable]);

	// Fall back to default map if selected map requires a key that isn't available
	useEffect(() => {
		if (!maptilerAvailable && baseMaps[selectedMap]?.requiresKey) setSelectedMap(FALLBACK_MAP);
	}, [maptilerAvailable, selectedMap, baseMaps]);

	// Close panel when clicking outside
	useEffect(() => {
		const handleClickOutside = e => {
			if (panelRef.current && !panelRef.current.contains(e.target)) setIsOpen(false);
		};

		if (isOpen) document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen]);

	// Create or update base map layer
	useEffect(() => {
		if (!mapObject) return;

		const existingLayer = getLayerByCustomId(mapObject, BASE_MAP_LAYER_ID);
		if (existingLayer) {
			mapObject.removeLayer(existingLayer);
			baseMapLayerRef.current = null;
		}

		const mapConfig = baseMaps[selectedMap];
		if (!maptilerKey && mapConfig?.requiresKey) return;
		if (!mapConfig?.xyz) return;

		const source = new XYZ({ ...mapConfig.xyz, crossOrigin: 'anonymous' });
		const layer = new TileLayer({
			source,
			zIndex: Z_INDEX,
			properties: { customLayerId: BASE_MAP_LAYER_ID },
			preload: Infinity,
		});

		// Disable all MapTiler maps and fall back to default if tiles fail to load (e.g. invalid API key)
		let hasFailed = false;
		const handleTileError = ({ tile, type }) => {
			if (hasFailed) return;
			hasFailed = true;

			const { src_: src } = tile ?? {};

			console.group('Basemap Error');
			console.warn(`Base map "${selectedMap}" ${type}. Disabling MapTiler maps.`);
			console.warn('Image Source:', src);
			console.groupEnd();

			setTileLoadFailed(true);
			setSelectedMap(FALLBACK_MAP);
		};
		source.on('tileloaderror', handleTileError);

		mapObject.addLayer(layer);
		baseMapLayerRef.current = layer;

		return () => {
			source.un('tileloaderror', handleTileError);
			mapObject.removeLayer(layer);
			if (baseMapLayerRef.current === layer) baseMapLayerRef.current = null;
		};
	}, [mapObject, selectedMap, baseMaps]);

	const handleMapSelect = useCallback(mapId => {
		hasUserSelected.current = true;
		setSelectedMap(mapId);
	}, []);

	const togglePanel = useCallback(() => {
		setIsOpen(prev => !prev);
	}, []);

	const SelectedIcon = baseMaps[selectedMap]?.icon;

	return (
		<SelectorContainer ref={panelRef}>
			{isOpen && (
				<SelectorPanel data-rail-panel>
					<PanelHeader>
						<PanelTitle>Base Map</PanelTitle>
					</PanelHeader>

					<OptionsList>
						{Object.values(baseMaps).map(map => {
							const Icon = map.icon;
							const isDisabled = map.requiresKey && !maptilerAvailable;
							return (
								<OptionButton
									key={map.id}
									$selected={selectedMap === map.id}
									disabled={isDisabled}
									title={
										isDisabled
											? 'MapTiler maps are currently unavailable'
											: undefined
									}
									onClick={() => handleMapSelect(map.id)}>
									<OptionIcon>{Icon && <Icon size={ICON_SIZE.sm} />}</OptionIcon>
									{map.name}
									{isDisabled && (
										<IndicatorText $variant="warning">
											Unavailable
										</IndicatorText>
									)}
								</OptionButton>
							);
						})}
					</OptionsList>
				</SelectorPanel>
			)}

			<SelectorButton
				type="button"
				onClick={togglePanel}
				title="Base map"
				aria-label="Base map"
				aria-expanded={isOpen}
				$indicatorColor={!maptilerAvailable && 'rgb(255 165 0)'}>
				{SelectedIcon ? <SelectedIcon /> : <FaMap />}
			</SelectorButton>
		</SelectorContainer>
	);
};

export default BaseMapSelector;
